import classNames from "classnames";
import {
  IWebsiteBuilderTabset,
  websiteBuilderGenerateTabsetDesignStyles,
} from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderTabset";
import React, { ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSectionEntityPreviewContext } from "../../sectionEntity/SectionEntityPreviewContext";
import { useWebsiteBuilderEditorContext } from "../../../../../websiteBuilderEditorContext";

interface IWebsiteBuilderTabsetViewProps {
  node: IWebsiteBuilderTabset;
  children: ReactNode;
  elementChildMoving: boolean;
}

export function WebsiteBuilderTabsetView(props: IWebsiteBuilderTabsetViewProps) {
  const { node, children, elementChildMoving } = props;

  const { t } = useTranslation();

  const { preview } = useSectionEntityPreviewContext();

  const { updateTabsetActiveTab, removeTabsetActiveTab } = useWebsiteBuilderEditorContext();

  const activeTabHeaderRef = useRef<HTMLDivElement>(null);

  const settings = node.container.settings;
  const designStyles = websiteBuilderGenerateTabsetDesignStyles();

  const previewSelectedTabId = preview?.type === "container" && preview.container.type === "tabset"
    ? preview.container.selectedTabId
    : null;

  const activeTabId = node.children.some(tab => tab.id === previewSelectedTabId)
    ? previewSelectedTabId
    : settings.defaultTabId;

  useEffect(() => {
    updateTabsetActiveTab(node.id, activeTabId);
  }, [node.id, activeTabId, updateTabsetActiveTab]);

  useEffect(() => {
    return () => removeTabsetActiveTab(node.id);
  }, [node.id, removeTabsetActiveTab]);

  useEffect(() => {
    activeTabHeaderRef.current?.scrollIntoView({
      block: "nearest",
      inline: "nearest",
    });
  }, [activeTabId]);

  return (
    <div
      className={classNames(designStyles.className, "editor-tabset")}
      style={{
        ...designStyles.styleProperties,
        ...(elementChildMoving ? { position: "static" } : {}),
      }}
    >
      {node.children.length === 0 ? (
        <div className="editor-tabset-instruction">
          <h3>{t("Add a tab")}</h3>
          <p>{t("Double-click to manage tabs")}</p>
        </div>
      ) : (
        <div
          className={designStyles.tabHeaderClassName}
          style={designStyles.tabHeaderStyleProperties}
        >
          {node.children.map(tab => (
            <div
              key={tab.id}
              ref={tab.id === activeTabId ? activeTabHeaderRef : null}
              className={classNames(designStyles.tabHeaderItemClassName, {
                [designStyles.tabHeaderItemActiveClassName]: tab.id === activeTabId,
              })}
            >
              {tab.settings.title}
            </div>
          ))}
        </div>
      )}
      {children}
    </div>
  );
}
